import React, { useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ProductContext } from '../../providers/ProductContext';
import { getDisplayImageUrl } from '../../utils/imageUtils';

const AIProductsSection = ({ enabled = true, featuredProducts }) => {
  const { products: allProducts } = useContext(ProductContext);

  // Productos elegidos por el admin, o los últimos 4 activos como fallback
  const products = useMemo(() => {
    if (Array.isArray(featuredProducts) && featuredProducts.length > 0) {
      return featuredProducts.slice(0, 4);
    }

    return [...allProducts]
      .filter(p => p.isActive !== false)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 4);
  }, [allProducts, featuredProducts]);

  const getImageUrl = (product) => {
    const img = product.images?.[0] || product.image;
    return getDisplayImageUrl(img);
  };

  if (!enabled || products.length === 0) return null;

  return (
    <section style={{
      padding: 'clamp(40px, 8vw, 80px) clamp(16px, 4vw, 40px)',
      background: 'var(--bg-white)'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        marginBottom: 'clamp(24px, 4vw, 40px)',
        maxWidth: '1200px',
        margin: '0 auto clamp(24px, 4vw, 40px)'
      }}>
        <div>
          <h2 style={{
            fontFamily: 'var(--font-heading)',
            fontSize: 'clamp(1.5rem, 3vw, 2.2rem)',
            fontWeight: 300,
            textTransform: 'uppercase',
            letterSpacing: '0.15em',
            color: 'var(--text-primary)',
            marginBottom: '4px'
          }}>
            Nuestros Elegidos
          </h2>
          <p style={{
            color: 'var(--text-muted)',
            fontSize: '0.9rem',
            margin: 0
          }}>
            Las prendas más buscadas de la temporada
          </p>
        </div>
        <Link to="/tienda" style={{
          fontSize: '0.75rem',
          fontWeight: 500,
          textTransform: 'uppercase',
          letterSpacing: '0.15em',
          color: 'var(--text-primary)',
          textDecoration: 'none',
          borderBottom: '1px solid var(--text-primary)',
          paddingBottom: '2px',
          transition: 'color 0.3s ease'
        }}>
          Ver Todo
        </Link>
      </div>

      {/* Products Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '4px',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {products.map(product => {
          const basePrice = product.basePrice || product.price;
          const finalPrice = product.discount > 0
            ? parseFloat((basePrice - basePrice * product.discount / 100).toFixed(2))
            : basePrice;

          return (
            <Link
              key={product._id}
              to={`/product/${product._id}`}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <div style={{ overflow: 'hidden', aspectRatio: '3/4', background: 'var(--bg-secondary)', position: 'relative' }}>
                <img
                  src={getImageUrl(product)}
                  alt={product.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                  onMouseEnter={e => e.target.style.transform = 'scale(1.04)'}
                  onMouseLeave={e => e.target.style.transform = 'scale(1)'}
                  onError={e => { e.target.src = '/placeholder-image.svg'; }}
                />
              </div>

              <div style={{ padding: '12px 4px 0' }}>
                <h3 style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.82rem',
                  fontWeight: 400,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: 'var(--text-primary)',
                  margin: '0 0 6px',
                  lineHeight: 1.4
                }}>
                  {product.name}
                </h3>

                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {product.discount > 0 && (
                    <span style={{
                      color: 'var(--text-light)',
                      fontSize: '0.78rem',
                      textDecoration: 'line-through'
                    }}>
                      ${basePrice}
                    </span>
                  )}
                  <span style={{
                    color: product.discount > 0 ? 'var(--brand-primary)' : 'var(--text-primary)',
                    fontSize: '0.85rem',
                    fontWeight: 500
                  }}>
                    ${product.discount > 0 ? finalPrice : basePrice}
                  </span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      <style>{`
        @media (max-width: 768px) {
          section > div:last-of-type {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          section > div:first-of-type {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 8px;
          }
        }
      `}</style>
    </section>
  );
};

export default AIProductsSection;
